// *** Important Note ***
// To have make the backward smooth and avoid loading interruption we need to create a backbuffer when loading the fragment from KVS.
// the startTimestamp requested by user will be calculated by the `seekStartPosition` which is the start position of the fragment in the buffer.
// the new `startTimestamp` will be `startTimestamp - seekStartPosition` to get the correct start time of the fragment in the buffer.
// *** How it works ***
// to handle backward control of seek-bar when the backward control is clicked and the value is less than the current time of the video but not undefined.
// check if the requested startTimestamp is in the fragment list of `hlsPlaylist` with checking the `programDateTime` of each fragment
// the `programDateTime` can not be equal to the requested `startTimestamp` because the `programDateTime` is the start time of the fragment
// therefore we need to check if the `startTimestamp` is between the `programDateTime` and `programDateTime + duration` of the fragment
// if the fragment is exist then just reduce `video.currentTime` by the `value` and reduce the player timer by the `value` as well
// if not continue to the next step.s

import dayjs from 'dayjs';
import { checkHLSEndTimestamp, seekStartPosition } from './helpers';
import { findVideoOffHole, isValidVideoCurrentTime } from './seek.util';
import { checkSeekBuffer } from './video.util';
import { endDiscontinuity } from './discontinuity.util';
import { tracer } from './print';

/**
 * Handle seeking rewind in buffer
 * @param {Vue} context
 * @param {*} video
 * @param {number} startTimestamp
 * @returns
 */
export async function seekBackwardTimestamp(
  context,
  video,
  startTimestamp,
  signal
) {
  tracer('seekBackwardTimestamp', {
    startTimestamp: dayjs(startTimestamp).format('HH:mm:ss'),
    waitingTimestampCallerId: context.waitingTimestampCallerId,
  });
  context.currentTimeIsChanging = true;
  try {
    // handle buffer seek if the buffer seek was available
    const bufferSeekAvailable = handleBufferSeekRewind(
      context,
      video,
      startTimestamp
    );
    if (!bufferSeekAvailable) {
      // handle rewind out buffer if the buffer seek was not available
      handleRewindOutBuffer(context, { startTimestamp }, signal);
    }
  } catch (error) {
    console.error('[ERROR][seekBackwardTimestamp]', error);
  } finally {
    context.currentTimeIsChanging = false;
  }
}

/**
 * Handle seeking rewind in buffer
 * @param {Vue} context
 * @param {*} video
 * @param {*} startTimestamp
 * @returns
 */
function handleBufferSeekRewind(context, video, startTimestamp) {
  const fragmentsArePlaying = isPlayingStream(context);

  let seekBufferDetails = null;
  if (context.hlsPlaylist.length) {
    seekBufferDetails = checkSeekBuffer(
      video,
      startTimestamp,
      context.hlsPlaylist
    );
    tracer('seekBackwardTimestamp/checkSeekBuffer', {
      canSeekBuffer: seekBufferDetails.canSeekBuffer,
      timeInFragment: seekBufferDetails.timeInFragment,
    });
  }
  const timeInFragment = seekBufferDetails?.timeInFragment;
  const canSeekBuffer = seekBufferDetails?.canSeekBuffer;
  // when fragments are playing, the discontinuity is disabled
  if (fragmentsArePlaying) {
    // whether any hole in the buffer
    const videoOffHole = findVideoOffHole(context, startTimestamp);
    if (videoOffHole) {
      tracer('seekBackwardTimestamp/findVideoOffHole', {
        ...videoOffHole,
      });
      console.warn('[WARN] A video-off hole detected on backward ', {
        ...videoOffHole,
      });
      context.handleGoToTimeFallInGapTime({
        startTimestamp,
        videoOffHole,
      });
      return true;
    }

    // if no hole in the buffer rewind in the buffer
    if (isValidVideoCurrentTime(timeInFragment) && canSeekBuffer) {
      tracer('seekBackwardTimestamp/handleSeekInBuffer', {
        videoTime: timeInFragment.videoCurrentTime,
        frag: timeInFragment.fragment,
      });
      handleSeekInBuffer(context, {
        video,
        startTimestamp,
        videoTime: timeInFragment.videoCurrentTime,
        frag: timeInFragment.fragment,
      });
      return true;
    }

    // if the discontinuity is enabled and the requested timestamp is available in the loaded fragment list, then end the discontinuity
    // if discontinuity is enabled and the requested timestamp is not available in the loaded fragment list, return false to handle backward out buffer
    // if discontinuity is not enabled, then return false to handle backward out buffer
  } else if (context.isDiscontinuity && timeInFragment) {
    const cpdSequence = context.getCpdSequence();
    tracer('seekBackwardTimestamp/endDiscontinuity', {
      videoTime: timeInFragment.videoCurrentTime,
      timeInFragment,
      cpdSequence,
      startTimestamp,
    });
    if (
      timeInFragment.fragment.codecChanged ||
      (timeInFragment.fragment.cc >= cpdSequence && cpdSequence !== undefined)
    ) {
      return false;
    }
    endDiscontinuity(context, {
      videoTime: timeInFragment.videoCurrentTime,
      startTimestamp,
    });
    return true;
  }

  // the request time is not in loaded buffer and there is no hole detected in loaded buffer
  return false;
}

/**
 * Handle seeking rewind out of buffer
 * @param {Vue} context
 * @param {number} startTimestamp
 * @returns
 */
export async function handleRewindOutBuffer(
  context,
  { startTimestamp },
  signal
) {
  const fragmentsArePlaying = isPlayingStream(context);

  const endTimestamp = checkHLSEndTimestamp(
    startTimestamp,
    context.endTimestamp
  );

  const startTimestampOfBackBuffer = dayjs(startTimestamp)
    .subtract(seekStartPosition, 'seconds')
    .valueOf();

  // check if the fragment is in the backbuffer

  let bufferCheck = null;
  try {
    bufferCheck = await context.kvService.checkBackBufferFragmentExist(
      startTimestamp,
      seekStartPosition,
      signal
    );
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      tracer('seekBackwardTimestamp/checkBackBufferFragmentExist', {
        error,
      });
      // ignore the error if the request is aborted and prevent the player from getting stuck
      return;
    } else {
      console.error(`
          [ERROR] Unkown error on checkBackBufferFragmentExist: ${error}
          `);
    }
  }

  const { startFragExist, fragments, useBackBuffer } = bufferCheck;

  //-------
  // Camera: NoFragmentFound - KVS Response: NoFragmentFound
  //-------
  // if no fragment found from KVS and the camera status is `NoFragmentFound` then just backward the timer
  //-------
  if (!startFragExist && context.isNoFragmentFound) {
    tracer('seekBackwardTimestamp/handleNoFragmentFound', {
      startTimestamp,
    });
    context.resetPlayerTimerWithNewTimestamp(startTimestamp);
    context.clearWaitingForNewTimestamp();
    return;
  }

  //-------
  // Camera: Playing - KVS Response: NoFragmentFound
  //-------
  // if the camera status is not one of the `NoFragmentFound` or `OutOfRetentionPeriod` or `Discontinuity` and startTimestamp is not in the fragment list
  // change the camera status to `Discontinuity` to pause the player and display `No Video` label
  //-------
  if (fragmentsArePlaying && !startFragExist) {
    tracer('seekBackwardTimestamp/handlePlayingNoFragmentFound', {
      startTimestamp,
    });
    // if the player is playing then pause the player and display `No Video` label on the player screen and change the camera status to `Discontinuity`
    // set player current time to zero because the player will resume from the start of the fragment
    context.timestamp = startTimestamp + 0;
    context.resetPlayerTimerWithNewTimestamp(startTimestamp);
    context.backwardGapTime =
      fragments?.[0] || context.hlsPlaylist?.[0] || context.backwardGapTime;
    context.waitingTimestampCallerId = 'seek:discontinuity';

    context.pausePlayerOnVideoOff();
    return;
  }

  //-------
  // Camera: Discontinuity - KVS Response: NoFragmentFound
  //-------
  // if the camera status is `Discontinuity` already and the startTimestamp is not in the fragment list which mean the player is paused already and display `No Video` label
  // then just change the discontinuity timer to the new timestamp
  //-------
  if (context.isDiscontinuity && !startFragExist) {
    tracer('seekBackwardTimestamp/handleDiscontinuityNoFragmentFound', {
      startTimestamp,
    });
    context.resetPlayerTimerWithNewTimestamp(startTimestamp);
    context.clearWaitingForNewTimestamp();
    context.waitingTimestampCallerId = 'seek:discontinuity';
    return;
  }

  /**
   * request for new playback session when the requested timestamp is not in the fragment list
   */
  const newFragRequest = {
    direction: 'backward',
    startTimestamp: useBackBuffer ? startTimestampOfBackBuffer : startTimestamp,
    endTimestamp,
    live: false,
    pauseAfterLoad: !context.isPlaying,
    noLoading: true,
    callerPath: 'seek/!value',
    startPosition: useBackBuffer ? seekStartPosition : undefined,
    skipFragCheck: true,
  };
  tracer('seekBackwardTimestamp/changeFragmentTime', {
    ...newFragRequest,
  });
  context.changeFragmentTime(newFragRequest);
}

/**
 * Check if the player is playing the stream
 * @param {Vue} context
 * @returns
 */
function isPlayingStream(context) {
  return !(
    context.isNoFragmentFound ||
    context.isDiscontinuity ||
    context.isOutOfRetentionPeriod
  );
}

/**
 * Handle seeking rewind in buffer
 * @param {Vue} context
 * @param {*} video
 * @param {number} startTimestamp
 * @param {number} videoTime
 * @param {*} frag
 * @returns
 */
function handleSeekInBuffer(
  context,
  { video, startTimestamp, videoTime, frag }
) {
  context.pausePlayerTimer();
  // check if it safe to rewind the player time to the requested startTimestamp if not it will request the new fragment from KVS
  if (!checkSafeSeekBuffer(context, { startTimestamp, frag })) {
    return;
  }
  // add the time elapsed to the player timer
  context.resetPlayerTimerWithNewTimestamp(startTimestamp);

  // set the new timestamp to the player video element
  video.currentTime = videoTime;

  // clear the waiting for new timestamp as the player time is changed
  context.$nextTick(() => {
    context.clearWaitingForNewTimestamp();
  });
}

function checkSafeSeekBuffer(context, { startTimestamp }) {
  const signal = context.getFragmentCheckSignal();
  // when there is an unexpected pause while the player is playing and the player is not paused by the user
  if (!context.checkVideoPlaying() && context.isPlaying) {
    handleRewindOutBuffer(
      context,
      {
        startTimestamp,
      },
      signal
    );
    return false;
  }

  return true;
}
